import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import { toast } from '../../common/Toast'
import GenericTable from '../../common/GenericTable'
import { capitalizeWords, groupModelsByDevice } from '../../../utils/functions'
import { clearVideosMessages, loadAdminVideoCategories, loadAdminVideoTypes, loadVideoModels, loadVideosUpload, removeVideo } from '../../../actions/videos'
import { formatDateDayMonthYear } from '../../../utils/filters/date'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'videoId',
    order: 'asc',
    translationKey: 'video_id_asc',
  },
  {
    field: 'videoId',
    order: 'desc',
    translationKey: 'video_id_desc',
  },
  {
    field: 'title',
    order: 'asc',
    translationKey: 'title_asc',
  },
  {
    field: 'title',
    order: 'desc',
    translationKey: 'title_desc',
  },
  {
    field: 'videoLastUpdateDate',
    order: 'asc',
    translationKey: 'last_updated_asc',
  },
  {
    field: 'videoLastUpdateDate',
    order: 'desc',
    translationKey: 'last_updated_desc',
  },
  {
    field: 'accessLevel',
    order: 'asc',
    translationKey: 'access_level_asc',
  },
  {
    field: 'accessLevel',
    order: 'desc',
    translationKey: 'access_level_desc',
  },
]

const AddFaultVideoModal = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { selectedVideos, models, handleUpdateVideos, closeModal } = props
  const [videosTableData, setVideosTableData] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showSpinner, setShowSpinner] = useState(null)
  const [expandedVideo, setExpandedVideo] = useState(0)

  useEffect(() => {
    const tableData = props.videos.map((video, index) => {
      return {
        index,
        identifier: video.id,
        videoId: video.videoId,
        title: video.title,
        lastUpdated: video.videoLastUpdateDate ? formatDateDayMonthYear(video.videoLastUpdateDate) : '',
        accessLevel: video.accessLevel,
        model: groupModelsByDevice(video.models || []),
      }
    })

    setVideosTableData(tableData)
  }, [props.videos])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadVideos(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        setSelectedPage(1)
        handleLoadVideos(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, orderBy])

  const handleLoadVideos = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadVideosUpload(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const createQueryParams = () => {
    let queryParams = {
      startPage: 1,
      category: 'Troubleshooting',
    }

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (models.length) {
      queryParams = { ...queryParams, model: models }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleRowClick = (videoId) => {
    const selectedVideo = props.videos.find((video) => video.id === videoId)
    const selectedVideosIds = selectedVideos.filter((video) => video).map((video) => video.id)

    if (selectedVideosIds.includes(selectedVideo.id)) {
      handleUpdateVideos(selectedVideos.map((video) => (video?.id === selectedVideo.id ? null : video)))
    } else {
      const index = selectedVideos.findIndex((video) => video === null)

      if (index !== -1) {
        const updatedVideos = [...selectedVideos]
        updatedVideos[index] = selectedVideo
        handleUpdateVideos(updatedVideos)

        return
      }

      toast.error(t('video_context.max_videos_selected'))
    }
  }

  return (
    <>
      <PageModal toggle onToggle={closeModal} className="uploads-modal videos-modal" title={t('video_context.select_video')}>
        <div className="uploads-modal-content">
          <div className="actions-wrapper">
            <SearchWithFilter onSearch={(e) => handleSearch(e)} />
          </div>
          {isMobile && videosTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
              />
            </div>
          )}
          {!isMobile && videosTableData.length > 0 && (
            <GenericTable
              data={videosTableData}
              headers={[
                {
                  title: t('video_id'),
                  orderKey: 'videoId',
                },
                {
                  title: t('title'),
                  orderKey: 'title',
                },
                {
                  title: t('access_level'),
                  orderKey: 'accessLevel',
                },
                {
                  title: t('model'),
                },
                {
                  title: capitalizeWords(t('last_updated')),
                  orderKey: 'videoLastUpdateDate',
                },
                {
                  title: '',
                },
              ]}
              keys={['videoId', 'title', 'accessLevel', 'model', 'lastUpdated', 'action']}
              keyRenderer={{
                accessLevel: (item) => t(item.accessLevel),
                action: (item) => {
                  return (
                    <div
                      className="table-actions-wrapper"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <SimpleButton className="action-button" onClick={() => handleRowClick(item.identifier)}>
                        {selectedVideos.map((v) => v && v.id).includes(item.identifier) ? t('remove') : t('add')}
                      </SimpleButton>
                    </div>
                  )
                },
              }}
              onRowClick={(video) => handleRowClick(video.identifier)}
              activeSort={orderBy}
              onSort={(orderingField) => handleSort(orderingField)}
              isLoading={showSpinner}
            />
          )}
          {isMobile &&
            videosTableData.map((video, index) => (
              <div key={index} className="card" onClick={() => setExpandedVideo(index)}>
                <div className={'card-item' + (expandedVideo !== index ? ' align-center' : '')}>
                  <div className="card-item-title">{t('title')}</div>
                  <div className={'card-item-body' + (expandedVideo !== index ? ' align-center' : '')}>
                    <div>{video.title}</div>
                    <div
                      className="card-actions"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    ></div>
                  </div>
                </div>
                {expandedVideo === index && (
                  <>
                    <div className="card-item">
                      <div className="card-item-title">{t('video_id')}</div>
                      <div className="card-item-body">{video.videoId}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('model')}</div>
                      <div className="card-item-body">{video.model}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('access_level')}</div>
                      <div className="card-item-body">{t(video.accessLevel)}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{capitalizeWords(t('last_updated'))}</div>
                      <div className="card-item-body">{video.lastUpdated}</div>
                    </div>
                    <div className="card-item">
                      <SimpleButton className="action-button" onClick={() => handleRowClick(video.identifier)}>
                        {selectedVideos.map((v) => v && v.id).includes(video.identifier) ? t('remove') : t('add')}
                      </SimpleButton>
                    </div>
                  </>
                )}
              </div>
            ))}
          {!showSpinner && !isFirstLoad && videosTableData.length === 0 && (
            <EmptyPageResults title={t('video_context.no_videos_found')} subtitle={t('cannot_find_matching_search')} />
          )}
          {canLoadMore && (
            <div className="buttons-wrapper">
              <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
                {t('load_more')}
              </SimpleButton>
            </div>
          )}
        </div>
      </PageModal>
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </>
  )
}

function stateToProps({ videos }) {
  return {
    videos: videos?.videosUpload || [],
    isLoading: videos?.isLoading,
    canLoadMore: videos?.canLoadMoreVideosUpload,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadVideosUpload,
        loadAdminVideoCategories,
        loadAdminVideoTypes,
        loadVideoModels,
        removeVideo,
        clearVideosMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddFaultVideoModal)
